import type { ComponentType } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { PermissionGate } from '@saas/ui';
import { CAMPAIGN_READ, CAMPAIGN_WRITE } from './nav.js';

type PageModule = { default: ComponentType };

function guarded(load: () => Promise<PageModule>, anyOf: string[]): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    const Page = m.default;
    const Guarded = () => (
      <PermissionGate anyOf={anyOf}>
        <Page />
      </PermissionGate>
    );
    return { Component: Guarded };
  };
}

/**
 * ログイン後（シェルの中・ModuleGate の下）。apps/web が集約する。ルート単位で遅延ロード（03 第 I 部 §2）。
 * 固定の経路（templates / settings / suppressions / new）は `:id` より優先される（react-router の順位付け）
 */
export const campaignRoutes: RouteObject[] = [
  { path: '/campaign', element: <Navigate to="/campaigns" replace /> },
  { path: '/campaigns', lazy: guarded(() => import('./pages/CampaignListPage.js'), CAMPAIGN_READ) },
  { path: '/campaigns/new', lazy: guarded(() => import('./pages/CampaignEditPage.js'), CAMPAIGN_WRITE) },
  { path: '/campaigns/templates', lazy: guarded(() => import('./pages/TemplatesPage.js'), CAMPAIGN_READ) },
  // 設定は read でも見られる（保存は campaign.settings.manage。送れない理由を担当者が確かめられるように）
  { path: '/campaigns/settings', lazy: guarded(() => import('./pages/CampaignSettingsPage.js'), CAMPAIGN_READ) },
  { path: '/campaigns/suppressions', lazy: guarded(() => import('./pages/SuppressionsPage.js'), CAMPAIGN_READ) },
  { path: '/campaigns/:id', lazy: guarded(() => import('./pages/CampaignDetailPage.js'), CAMPAIGN_READ) },
  { path: '/campaigns/:id/edit', lazy: guarded(() => import('./pages/CampaignEditPage.js'), CAMPAIGN_WRITE) },
];

/**
 * ログイン不要の配信停止ページ（SFA §9.6）。URL は ${APP_URL}/u/<token>（送信の差し込み `{{unsubscribeUrl}}`・campaign-token.ts の unsubscribePagePath）。
 * `apps/web/src/app/router.tsx` で calendar の `/b/:token` と同じ場所 — PublicOnly / RequireAuth / Shell / ModuleGate の外 — に並べる
 */
export const campaignPublicRoutes: RouteObject[] = [
  {
    path: '/u/:token',
    lazy: async () => {
      const m: PageModule = await import('./pages/PublicUnsubscribePage.js');
      return { Component: m.default };
    },
  },
];
